import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, EyeOff, RotateCcw, ExternalLink } from "lucide-react";
import api from "@/lib/api";

export default function AdminEvents() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (search) params.set("search", search);
      const r = await api.get(`/admin/events?${params.toString()}`);
      setRows(r.data?.events || []); setTotal(r.data?.total || 0);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [search]);

  const takedown = async (ev) => {
    const reason = window.prompt(`Take down "${ev.honoree_name}"? Enter reason (visible in audit log):`);
    if (!reason) return;
    try { await api.post(`/admin/events/${ev.event_id}/takedown`, { reason }); toast.success("Taken down"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const restore = async (ev) => {
    try { await api.post(`/admin/events/${ev.event_id}/restore`); toast.success("Restored"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 sm:p-8 max-w-6xl" data-testid="admin-events">
      <h1 className="font-display text-2xl font-semibold">Events</h1>
      <p className="text-sm text-[hsl(var(--muted-foreground))]">{total} total</p>

      <div className="mt-5 relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
        <input data-testid="admin-events-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search honoree or slug" className="w-full h-10 pl-9 pr-3 rounded-xl bg-[hsl(var(--card))] border border-[hsl(var(--input))] text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
      </div>

      <div className="mt-5 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--muted))] text-left text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
            <tr>
              <th className="px-4 py-2 font-semibold">Honoree</th>
              <th className="px-4 py-2 font-semibold">Type</th>
              <th className="px-4 py-2 font-semibold">Status</th>
              <th className="px-4 py-2 font-semibold text-right">Tributes</th>
              <th className="px-4 py-2 font-semibold">Owner</th>
              <th className="px-4 py-2 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="px-4 py-6 text-center text-xs text-[hsl(var(--muted-foreground))]">Loading…</td></tr>
            : rows.length === 0 ? <tr><td colSpan={6} className="px-4 py-6 text-center text-xs text-[hsl(var(--muted-foreground))]">No events match.</td></tr>
            : rows.map((ev) => (
              <tr key={ev.event_id} data-testid={`admin-event-row-${ev.event_id}`} className="border-t border-[hsl(var(--border))]">
                <td className="px-4 py-2.5"><div className="font-semibold">{ev.honoree_name}</div><div className="text-[11px] text-[hsl(var(--muted-foreground))]">/{ev.slug}</div></td>
                <td className="px-4 py-2.5 capitalize text-xs">{ev.type}</td>
                <td className="px-4 py-2.5 text-xs">{ev.taken_down ? <span className="text-[hsl(var(--destructive))] font-semibold">Taken down</span> : ev.status}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{ev.submission_count || 0}</td>
                <td className="px-4 py-2.5 text-[11px] text-[hsl(var(--muted-foreground))] truncate max-w-[180px]">{ev.owner_email}</td>
                <td className="px-4 py-2.5 text-right">
                  <div className="inline-flex gap-1">
                    <a href={`/t/${ev.slug}`} target="_blank" rel="noopener noreferrer" className="h-8 w-8 grid place-items-center rounded-lg hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]" aria-label="Open tribute"><ExternalLink size={14} /></a>
                    {ev.taken_down ? (
                      <button data-testid={`admin-event-restore-${ev.event_id}`} onClick={() => restore(ev)} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-[hsl(var(--muted))]" aria-label="Restore"><RotateCcw size={14} /></button>
                    ) : (
                      <button data-testid={`admin-event-takedown-${ev.event_id}`} onClick={() => takedown(ev)} className="h-8 w-8 grid place-items-center rounded-lg text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10" aria-label="Takedown"><EyeOff size={14} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
